import React, { useCallback } from 'react'
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Button, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'

const CustomTooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, tooltipProps, size }) => {
  const { t } = useTranslation()
  return (
    <Box
      {...tooltipProps}
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        maxWidth: 380,
        p: 2.5,
        position: 'relative'
      }}
    >
      <IconButton
        {...closeProps}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'text.disabled' }}
      >
        <Close sx={{ fontSize: 18 }} />
      </IconButton>

      {step.title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, pr: 3, color: 'primary.main' }}>
          {step.title}
        </Typography>
      )}

      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2 }}>
        {step.content}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {index + 1} / {size}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {index > 0 && (
            <Button {...backProps} size="small" sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t('mailTemplate.campaigns.tour.back')}
            </Button>
          )}
          {continuous && (
            <Button
              {...primaryProps}
              size="small"
              variant="contained"
              sx={{
                textTransform: 'none',
                backgroundColor: '#0FC6B9',
                '&:hover': { backgroundColor: '#0DAB9F' },
                borderRadius: 1.5,
                px: 2
              }}
            >
              {index === size - 1
                ? t('mailTemplate.campaigns.tour.finish')
                : t('mailTemplate.campaigns.tour.next')
              }
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const CampaignTour = ({ run, onFinish, onStepChange }) => {
  const { t } = useTranslation()

  const steps = [
    {
      target: '[data-tour="campaign-page"]',
      title: t('mailTemplate.campaigns.tour.welcomeTitle'),
      content: t('mailTemplate.campaigns.tour.welcomeContent'),
      placement: 'center',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-action-checkRelevancy"]',
      title: t('mailTemplate.campaigns.tour.step1Title'),
      content: t('mailTemplate.campaigns.tour.step1Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-action-searchContacts"]',
      title: t('mailTemplate.campaigns.tour.step2Title'),
      content: t('mailTemplate.campaigns.tour.step2Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-badge-contacts"]',
      title: t('mailTemplate.campaigns.tour.step3Title'),
      content: t('mailTemplate.campaigns.tour.step3Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-action-generateContent"]',
      title: t('mailTemplate.campaigns.tour.step4Title'),
      content: t('mailTemplate.campaigns.tour.step4Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-badge-content"]',
      title: t('mailTemplate.campaigns.tour.step5Title'),
      content: t('mailTemplate.campaigns.tour.step5Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-preview-email"]',
      title: t('mailTemplate.campaigns.tour.step5bTitle'),
      content: t('mailTemplate.campaigns.tour.step5bContent'),
      placement: 'left',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-firm-menu"]',
      title: t('mailTemplate.campaigns.tour.deleteFirmTitle'),
      content: t('mailTemplate.campaigns.tour.deleteFirmContent'),
      placement: 'left',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-edit-firms"]',
      title: t('mailTemplate.campaigns.tour.editFirmsTitle'),
      content: t('mailTemplate.campaigns.tour.editFirmsContent'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-send-emails"]',
      title: t('mailTemplate.campaigns.tour.step6Title'),
      content: t('mailTemplate.campaigns.tour.step6Content'),
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-tour="campaign-limits"]',
      title: t('mailTemplate.campaigns.tour.limitsTitle'),
      content: t('mailTemplate.campaigns.tour.limitsContent'),
      placement: 'right',
      disableBeacon: true
    }
  ]

  const handleCallback = useCallback((data) => {
    const { status, action, type, index } = data
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      onStepChange?.(index + 1)
    } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      onStepChange?.(index - 1)
    } else if (type === EVENTS.STEP_BEFORE) {
      onStepChange?.(index)
    }
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      (type === EVENTS.STEP_AFTER && action === ACTIONS.CLOSE)
    ) {
      onStepChange?.(-1)
      onFinish?.()
    }
  }, [onFinish, onStepChange])

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      scrollOffset={80}
      callback={handleCallback}
      tooltipComponent={CustomTooltip}
      locale={{
        skip: t('mailTemplate.campaigns.tour.skip')
      }}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.4)'
        }
      }}
      floaterProps={{
        disableAnimation: true
      }}
    />
  )
}

export default CampaignTour
